import { Injectable } from '@angular/core';
import { WorkItemListenerService } from 'common/workitems/workitem-listener.service';
import { DesktopPopupService } from 'common/desktop-popup-service/desktop-popup.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { DiscussionsMessagesTypes } from '../../components/discussions/discussion-properties';
import { bufferTime, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionGroupModel } from './subscription-group.model';
import { BcAuth } from 'common/auth/auth.service';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { UserContextService } from 'common/user-context/user-context.service';
import { NotificationResult } from 'shared/models/notification/notification-result.model';
import { WindowService } from 'shared/services/window/window.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { VideoSessionStatus } from 'common/common/constants/video-session-status';
import { Subscription } from 'rxjs';
import { BoldItem } from 'common/workitems/interfaces/workitem-bold.interface';
import { TextChat } from 'common/xislivechat/models/text-chat';
import { WorkItem } from 'common/workitems/interfaces/workitem-base.interface';
import { EmailItem } from 'common/workitems/interfaces/email-item.interface';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { TextChatMessage } from 'common/xislivechat/models/text-chat-message';

@Injectable()
export class WorkItemNotificationsService {
  public discussionsObservable;

  public chatSubscriptionGroup = new SubscriptionGroupModel();
  public emailSubscriptionGroup = new SubscriptionGroupModel();
  public smsSubscriptionGroup = new SubscriptionGroupModel();
  public facebookSubscriptionGroup = new SubscriptionGroupModel();
  public asyncMessageSubscriptionGroup = new SubscriptionGroupModel();
  public abcSubscriptionGroup = new SubscriptionGroupModel();
  public discussionsSubscriptionGroup = new SubscriptionGroupModel();

  public chatMessagesSubscriptions = {};
  public textChatMessagesSubscription = {};
  public discussionMessagesSubscriptions = {};
  public chatDiscussionMessagesSubscriptions = {};

  public chatQueueSubscriptionGroup = new SubscriptionGroupModel();
  public chatVideoStatusSubscription: Subscription;

  private _chatQueueCount = 0;
  private _chatTakeNextTimeoutHandle: number;

  private readonly BUFFER_TIME = 300;
  private readonly TAKE_NEXT_TIMEOUT = 1000;

  private _operatorId;

  constructor(private _bcAuth: BcAuth,
              private _bcWorkItemListener: WorkItemListenerService,
              private _desktopPopupService: DesktopPopupService,
              private _translateService: TranslateService,
              private _userContextService: UserContextService,
              private _rxEmitter: RxEmitter,
              private _windowService: WindowService,
              private _bcSettingsService: BcSettingsService) {
  }

  public async initNotificationSubscriptions() {
    const settings = await this._bcSettingsService.getSettings();
    const isVideoFeatureEnabled = this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_VIDEO_CHAT)
      && this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_NEW_VIDEO_CHAT);
    this._operatorId = this._bcAuth.getSessionUserId();

    this.setupChatNotifications(isVideoFeatureEnabled);
    this.setupEmailNotifications();
    this.setupTextChatNotifications();
    this.setupDiscussionsNotifications();
  }

  public setupChatNotifications(isVideoFeatureEnabled: boolean = false) {
    let chatInitialFinished: boolean = false;

    this._bcWorkItemListener.createPromiseForInitialLoadingDone().then(() => {
      chatInitialFinished = true;
    });

    this.chatSubscriptionGroup.insert = this._bcWorkItemListener.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.CHAT).subscribe((item: WorkItem & ChatItem) => {
      this._createChatMessagesSubscriptionForChat(item);
      this._createChatDiscussionMessagesSubscriptionForChat(item);
      if (chatInitialFinished) {
        this._sendTranslatedNotification('liveChat.desktopNotifications.newChat', { senderName: item.Name });
      }
    });

    this.chatSubscriptionGroup.remove = this._bcWorkItemListener.createAcdBasedObservableForRemoveByWorkItemType(WorkItemTypes.CHAT).subscribe((item: WorkItem & ChatItem) => {
      this._clearSingleSubscription(this.chatMessagesSubscriptions, item.ItemID);
      this._clearSingleSubscription(this.chatDiscussionMessagesSubscriptions, item.ItemID);
    });

    const isUseAnswerNextEnabled = this._userContextService.userContext.operatorContext.useAnswerNext;
    if (isUseAnswerNextEnabled) {
      this.chatQueueSubscriptionGroup.insert = this._bcWorkItemListener.observeAnswerableWorkItemsByLoginIdByWorkItemType(WorkItemTypes.CHAT).inserted()
        .subscribe(() => {
          if (this._chatQueueCount === 0) {
            this._scheduleTakeNextNotification();
          }

          this._chatQueueCount++;
        }, console.error);

      this.chatQueueSubscriptionGroup.remove = this._bcWorkItemListener.observeAnswerableWorkItemsByLoginIdByWorkItemType(WorkItemTypes.CHAT).deleted()
        .subscribe(() => {
          this._chatQueueCount--;
        }, console.error);
    }

    if (isVideoFeatureEnabled) {
      this.chatVideoStatusSubscription = this._bcWorkItemListener.observeActiveChatsForVideoSessionStatus().subscribe(chatItem => {
        if (chatItem.VideoSessionStatus === VideoSessionStatus.InitiatedByVisitor) {
          this._sendTranslatedNotification('liveChat.desktopNotifications.incomingVideoCall', { name: chatItem.ChatName });
        }
      });
    }
  }

  public setupEmailNotifications() {
    let emailsInitialFinished: boolean = false;

    this._bcWorkItemListener.createPromiseForInitialLoadingDone().then(() => {
      emailsInitialFinished = true;
    });

    this.emailSubscriptionGroup.insert = this._bcWorkItemListener.createAcdBasedObservableForInsertByWorkItemType(WorkItemTypes.EMAIL).subscribe((item: WorkItem & EmailItem) => {
      if (emailsInitialFinished) {
        this._sendTranslatedNotification('liveChat.desktopNotifications.newEmail', { senderName: item.Name });
      }
    });
  }

  public setupTextChatNotifications() {
    this._setupTextChatNotifications(this.smsSubscriptionGroup, WorkItemTypes.SMS);
    this._setupTextChatNotifications(this.facebookSubscriptionGroup, WorkItemTypes.FACEBOOK);
    this._setupTextChatNotifications(this.asyncMessageSubscriptionGroup, WorkItemTypes.ASYNC_MESSAGE);
  }

  public setupDiscussionsNotifications() {
    let discussionInitialFinished: boolean = false;

    this.discussionsObservable = this._bcWorkItemListener.observeActiveDiscussionsByLoginId();
    this.discussionsObservable.done().pipe(take(1)).subscribe(() => {
      discussionInitialFinished = true;
    });

    this.discussionsSubscriptionGroup.insert = this.discussionsObservable.inserted().subscribe((item) => {
      this._createDiscussionMessagesSubscription(item);
      if (discussionInitialFinished) {
        this._sendTranslatedNotification('liveChat.desktopNotifications.incomingDiscussion', { senderName: item.OperatorName });
      }
    });

    this.discussionsSubscriptionGroup.remove = this.discussionsObservable.deleted().subscribe((item) => {
      this._clearSingleSubscription(this.discussionMessagesSubscriptions, item.WorkItemID);
    });
  }

  private _scheduleTakeNextNotification(): void {
    if (this._chatTakeNextTimeoutHandle) {
      clearTimeout(this._chatTakeNextTimeoutHandle);
    }

    this._chatTakeNextTimeoutHandle = this._windowService.getWindow().setTimeout(() => {
      if (this._chatQueueCount > 0) {
        this._sendTakeNextNotification();
      }
    }, this.TAKE_NEXT_TIMEOUT);
  }

  private _sendTakeNextNotification(): void {
    this._sendTranslatedNotification('liveChat.desktopNotifications.newChatInQueue', {}, {
      actions: [
        {
          action: 'takeNext',
          title: 'workitemAnswerNext.answerNext.chat.takeNext'
        }
      ]
    }).then(result => {
      if (result.action === 'takeNext') {
        this._rxEmitter.send(MessageBusEvents.TakeNext, { workItemType: WorkItemTypes.CHAT });
      }
    });
  }

  private _createChatMessagesSubscriptionForChat(chatItem) {
    let chatInitialFinished: boolean = false;
    const chatMessagesObservable = this._bcWorkItemListener.createObservableForChatMessagesByChatId(chatItem.ItemID);
    chatMessagesObservable.done().pipe(take(1)).subscribe(() => {
      chatInitialFinished = true;
    });

    const bufferedObservable = chatMessagesObservable.inserted().pipe(bufferTime(this.BUFFER_TIME));
    this.chatMessagesSubscriptions[chatItem.ItemID] = bufferedObservable.subscribe((workItemMessages) => {
      if (chatInitialFinished && workItemMessages.length === 1 && workItemMessages[workItemMessages.length - 1].Name !== null && workItemMessages[workItemMessages.length - 1].PersonID !== this._operatorId) {
        const translateParams = {
          senderName: workItemMessages[workItemMessages.length - 1].Name,
          sentText: this._htmlToPlaintext(workItemMessages[workItemMessages.length - 1].Text)
        };
        this._sendTranslatedNotification('liveChat.desktopNotifications.incomingChatMessage', translateParams);
      }
    });
  }

  private _createChatDiscussionMessagesSubscriptionForChat(chatItem) {
    let chatDiscussionMessagesInitialFinished: boolean = false;

    const chatDiscussionMessagesObservable = this._bcWorkItemListener.createObservableForDiscussionsMessagesByChatId(chatItem.ItemID);
    chatDiscussionMessagesObservable.done().pipe(take(1)).subscribe(() => {
      chatDiscussionMessagesInitialFinished = true;
    });

    const bufferedObservable = chatDiscussionMessagesObservable.inserted().pipe(bufferTime(this.BUFFER_TIME));
    this.chatDiscussionMessagesSubscriptions[chatItem.ItemID] = bufferedObservable.subscribe((chatDiscussionMessages) => {
      if (chatDiscussionMessagesInitialFinished && chatDiscussionMessages.length === 1 && chatDiscussionMessages[chatDiscussionMessages.length - 1].MessageType === DiscussionsMessagesTypes.OPERATOR) {
        const translateParams = {
          senderName: chatDiscussionMessages[chatDiscussionMessages.length - 1].OperatorName,
          sentText: this._formatDiscussionMessage(chatDiscussionMessages[chatDiscussionMessages.length - 1].DiscussionMessage)
        };
        this._sendTranslatedNotification('liveChat.desktopNotifications.incomingDiscussionMessage', translateParams);
      }
    });
  }

  private _setupTextChatNotifications(subscriptionGroup, workItemType: WorkItemTypes) {
    let initialFinished: boolean = false;

    this._bcWorkItemListener.createPromiseForInitialLoadingDone().then(() => {
      initialFinished = true;
    });

    subscriptionGroup.insert = this._bcWorkItemListener.createAcdBasedObservableForInsertByWorkItemType(workItemType).subscribe((item: WorkItem & TextChat) => {
      this._createTextChatMessagesSubscription(item);
      if (initialFinished) {
        this._sendTranslatedNotification('liveChat.desktopNotifications.newTextChat', { senderName: item.Name });
      }
    });

    subscriptionGroup.remove = this._bcWorkItemListener.createAcdBasedObservableForRemoveByWorkItemType(workItemType).subscribe((item: WorkItem & BoldItem) => {
      this._clearSingleSubscription(this.textChatMessagesSubscription, item.ItemID);
    });
  }

  private _createTextChatMessagesSubscription(textChatWorkItem) {
    let textChatMessagesInitialFinished: boolean = false;

    const textChatMessagesObservable = this._bcWorkItemListener.createObservableForTextChatMessages(textChatWorkItem);

    textChatMessagesObservable.done().subscribe(() => {
      textChatMessagesInitialFinished = true;
    });

    this.textChatMessagesSubscription[textChatWorkItem.WorkItemID] = textChatMessagesObservable.inserted().subscribe((textChatMessage: TextChatMessage) => {
      if (textChatMessagesInitialFinished) {
        const translateParams = {
          senderName: textChatMessage.Name,
          sentText: textChatMessage.Text
        };
        this._sendTranslatedNotification('liveChat.desktopNotifications.incomingTextChat', translateParams);
      }
    });
  }

  private _createDiscussionMessagesSubscription(discussion) {
    let discussionMessagesInitialFinished: boolean = false;

    const discussionMessagesObservable = this._bcWorkItemListener.createObservableForDiscussionsMessagesByChatId(discussion.WorkItemID);
    discussionMessagesObservable.done().pipe(take(1)).subscribe(() => {
      discussionMessagesInitialFinished = true;
    });

    const bufferedObservable = discussionMessagesObservable.inserted().pipe(bufferTime(this.BUFFER_TIME));
    this.discussionMessagesSubscriptions[discussion.WorkItemID] = bufferedObservable.subscribe((messageItems) => {
      if (discussionMessagesInitialFinished && messageItems.length === 1 && messageItems[messageItems.length - 1].MessageType === DiscussionsMessagesTypes.OPERATOR) {
        const translateParams = {
          senderName: messageItems[messageItems.length - 1].OperatorName,
          sentText: this._formatDiscussionMessage(messageItems[messageItems.length - 1].DiscussionMessage)
        };
        this._sendTranslatedNotification('liveChat.desktopNotifications.incomingDiscussionMessage', translateParams);
      }
    });
  }

  public clearSubscriptions() {
    this._clearSubscriptionsInGroup(this.chatMessagesSubscriptions);
    this._clearSubscriptionsInGroup(this.chatDiscussionMessagesSubscriptions);
    this._clearSubscriptionsInGroup(this.discussionMessagesSubscriptions);
    this._clearSubscriptionsInGroup(this.textChatMessagesSubscription);

    this._clearSubscriptionGroup(this.chatSubscriptionGroup);
    this._clearSubscriptionGroup(this.emailSubscriptionGroup);
    this._clearSubscriptionGroup(this.smsSubscriptionGroup);
    this._clearSubscriptionGroup(this.facebookSubscriptionGroup);
    this._clearSubscriptionGroup(this.asyncMessageSubscriptionGroup);
    this._clearSubscriptionGroup(this.abcSubscriptionGroup);
    this._clearSubscriptionGroup(this.discussionsSubscriptionGroup);

    this._clearSubscriptionGroup(this.chatQueueSubscriptionGroup);

    if (this.chatVideoStatusSubscription) {
      this.chatVideoStatusSubscription.unsubscribe();
      this.chatVideoStatusSubscription = null;
    }
  }

  private _clearSubscriptionGroup(subscriptionGroup) {
    if (subscriptionGroup.insert) {
      subscriptionGroup.insert.unsubscribe();
      subscriptionGroup.insert = null;
    }
    if (subscriptionGroup.remove) {
      subscriptionGroup.remove.unsubscribe();
      subscriptionGroup.remove = null;
    }
  }

  private _clearSingleSubscription(subscriptionsList, workItemId) {
    if (subscriptionsList[workItemId]) {
      subscriptionsList[workItemId].unsubscribe();
      subscriptionsList[workItemId] = null;
    }
  }

  private _clearSubscriptionsInGroup(subscription) {
    for (let key of Object.keys(subscription)) {
      if (subscription[key]) {
        subscription[key].unsubscribe();
        subscription[key] = null;
      }
    }
  }

  private _sendTranslatedNotification(translateKey: string, translateParams: object, extraOptions?: NotificationOptions): Promise<NotificationResult> {
    let translatedText = this._translateService.instant(translateKey, translateParams);
    let translatedOptions = this._getTranslatedOptions(extraOptions, translateParams);
    return this._desktopPopupService.sendNotification(translatedText, translatedOptions);
  }

  private _getTranslatedOptions(extraOptions: NotificationOptions, translateParams: object): NotificationOptions {
    const options = extraOptions || {};
    return {
      ...options,
      body: options.body ? this._translateService.instant(options.body, translateParams) : undefined,
      actions: this._translateActions(options.actions || [], translateParams)
    };
  }

  private _formatDiscussionMessage(text) {
    return text.replace(/<\/?[^>]+(>|$)/g, '');
  }

  private _htmlToPlaintext(text) {
    return text && text.replace ? text.replace(/<[^>]+>/gm, '') : '';
  }

  private _translateActions(actions: NotificationAction[], translateParams: object) {
    if (actions) {
      return actions.map(action => {
        action.title = this._translateService.instant(action.title, translateParams);
        return action;
      });
    } else {
      return [];
    }
  }
}
